import { makeAutoObservable } from 'mobx'
import * as translations from '../translations/index'
import {
  config as translationsNewConfig,
  locales as translationsNewLocales,
  source as translationsNewSource,
  LocaleId,
} from '../translationsNew/index'
import { Language, TranslationsInterface } from '../types/translations'
import { TranslationsInterface as TranslationsNewInterface } from '../types/translationsNew'
import { RootStore } from './Root.store'
import { devLog } from '@/methods/devLog'
import { capitalize } from '@/methods/string'

const clientLangThemeMap = {
  bmwTheme: 'bmw',
  cmcTheme: 'cmc',
  enpalTheme: 'enpal',
  ikanoTheme: 'ikano',
  miniTheme: 'bmw',
  onventisTheme: 'onv',
  santanderTheme: 'sant',
}

function deepMerge<T extends Record<string, unknown>>(
  base: T,
  override?: Record<string, unknown>,
  ignoreEmpty = false
): T {
  if (!override) {
    return base
  }

  const result = { ...base }

  for (const key in override) {
    const baseValue = base[key]
    const overrideValue = override[key]

    if (
      overrideValue !== null &&
      typeof overrideValue === 'object' &&
      !Array.isArray(overrideValue) &&
      baseValue !== null &&
      typeof baseValue === 'object' &&
      !Array.isArray(baseValue)
    ) {
      result[key as keyof T] = deepMerge(
        baseValue as Record<string, unknown>,
        overrideValue as Record<string, unknown>,
        ignoreEmpty
      ) as T[keyof T]
    } else if (overrideValue !== undefined && !(ignoreEmpty && overrideValue === '')) {
      result[key as keyof T] = overrideValue as T[keyof T]
    }
  }

  return result
}

// Legacy resolution for the old `translations` set; removed together with that set (sc-21382).
function resolveLanguage(language: string, themeId?: string, configurationId?: string): string {
  if (!themeId) return language

  // get client name from themeId
  const client = clientLangThemeMap[themeId as keyof typeof clientLangThemeMap]
  // get custom client language name
  const computedLanguage = `${language}${capitalize(client)}`

  if (configurationId === 'bmw.ch' && language === 'de') {
    return 'deChBmw'
  }
  // verify if the custom client language exists
  else if (client && translations[computedLanguage as keyof typeof translations]) {
    return computedLanguage
  } else {
    return language
  }
}

// Picks the translationsNew locale, e.g. `de` + enpalTheme -> `de-enpal`.
function resolveLocale(language: string, themeId?: string, configurationId?: string): string {
  if (!themeId) return language

  const override = translationsNewConfig.configurationOverrides.find(
    (o) => o.configurationId === configurationId && o.language === language
  )
  if (override) return override.locale

  const client =
    translationsNewConfig.clients[themeId as keyof typeof translationsNewConfig.clients]
  const brandLocale = `${language}-${client}`

  return client && brandLocale in translationsNewConfig.locales ? brandLocale : language
}

// Merges en <- ...fallback chain <- locale. Unknown locales get plain English, and "" never
// overrides a fallback value.
function resolveTranslationsNew(locale: string): TranslationsNewInterface {
  if (!(locale in translationsNewConfig.locales)) return translationsNewSource

  const id = locale as LocaleId
  const chain = [id, ...(translationsNewConfig.locales[id] as LocaleId[])]
    .filter((l) => l !== translationsNewConfig.source)
    .reverse()

  return chain.reduce(
    (result, l) => deepMerge(result, translationsNewLocales[l] as Record<string, unknown>, true),
    translationsNewSource as unknown as Record<string, unknown>
  ) as unknown as TranslationsNewInterface
}

export class TranslationsStore {
  private rootStore: RootStore
  translations: TranslationsInterface = translations.en
  translationsNew: TranslationsNewInterface = translationsNewSource
  language = 'en'
  paramLanguage = 'en'

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  setTranslations(baseLanguage: Language, configurationId?: string): void {
    this.language = baseLanguage.substring(0, 2)

    const lang = resolveLanguage(
      this.language,
      this.rootStore.InterfaceState.themeId,
      configurationId
    )

    const locale = resolveLocale(
      this.language,
      this.rootStore.InterfaceState.themeId,
      configurationId
    )

    devLog('Resolved translation property: ', lang, locale)

    this.paramLanguage = lang
    document.documentElement.lang = this.language

    this.translations = deepMerge(translations.en, translations[lang as keyof typeof translations])
    this.translationsNew = resolveTranslationsNew(locale)
  }
}
